<template>
  <LmDialog v-if="modalShow && !(showProductTypeForm || showProviderForm)" v-model="modalShow" title="Create product">
    <template #toolbar-actions>
      <q-btn color="tertiary" @click="submit">
        Save
      </q-btn>
    </template>

    <template #content>
      <CreateSubjectCategoryModal v-model="createSubjectCategoryModal" @save="createSubjectCategory" />

      <q-splitter :value="splitterModel" :disable="true" style="overflow: auto">
        <template #before>
          <q-tabs v-model="activeTab" vertical class="text-teal">
            <q-tab name="basic" label="Basic" />
            <q-tab name="metaData" label="Metadata" />
          </q-tabs>
        </template>

        <template #after>
          <q-tab-panels v-model="activeTab" keep-alive animated transition-prev="scale" transition-next="scale">
            <q-tab-panel name="basic">
              <q-card-section :style="{ height: '600px' }">
                <form ref="formData" class="form">
                  <div class="row q-col-gutter-md q-mb-md wrap">
                    <div class="col-xs-5">
                      <div class="col-auto">
                        <enhanced-q-select
                          id="publisher"
                          v-model="$v.formData.publisher_id.$model"
                          :error="$v.formData.publisher_id.$error"
                          :error-message="$v_displayMessage($v.formData.publisher_id)"
                          :disable="!!publisher"
                          :options="publisherOptions"
                          :label="getTranslation('product.fields.publisher') || 'Publisher *'"
                          hide-bottom-space
                        />
                        <div class="col-auto q-pt-sm">
                          <LinkButton text="Create new publisher" @click="openPublisherForm" />
                        </div>
                      </div>
                    </div>

                    <div class="col-xs-4">
                      <div class="col-auto">
                        <enhanced-q-select
                          id="type"
                          ref="productTypeSelect"
                          v-model="formData.product_type_id"
                          :options="typeOptions"
                          :label="getTranslation('product.fields.type') || 'Product Type'"
                        />
                      </div>
                      <div class="col-auto q-pt-sm">
                        <LinkButton text="Create new type" @click="openPopupField = true" />
                        <PopupField
                          :styles="{ left: '125px', top: '-10px' }"
                          :open="openPopupField"
                          :loading="creatingProductType"
                          title="New type"
                          @close="openPopupField = false"
                          @save="createProductType(productTypeName)"
                        >
                          <template #content>
                            <q-input slot="field" v-model="productTypeName" type="text" class="col" outlined dense />
                          </template>
                        </PopupField>
                      </div>
                    </div>

                    <div class="col-xs-3">
                      <q-input
                        id="platform"
                        v-model="$v.formData.platform.$model"
                        :error="$v.formData.platform.$error"
                        :error-message="$v_displayMessage($v.formData.platform)"
                        :label="getTranslation('product.fields.platform') || 'Platform'"
                        type="text"
                        outlined
                      />
                    </div>

                    <div class="col-xs-5">
                      <q-input
                        id="name"
                        v-model="$v.formData.name.$model"
                        :error="$v.formData.name.$error"
                        :error-message="$v_displayMessage($v.formData.name)"
                        :label="getTranslation('product.fields.name') || 'Name *'"
                        type="text"
                        outlined
                      />
                    </div>

                    <div class="col-xs-3">
                      <enhanced-q-select
                        id="owner"
                        v-model="formData.owner_id"
                        :options="userOptions"
                        :label="getTranslation('product.fields.owner') || 'Owner'"
                      />
                    </div>

                    <div class="col-xs-3">
                      <enhanced-q-select
                        id="subject-category"
                        v-model="formData.main_subject_category_id"
                        :options="subjectCategoriesOptions"
                        label="Subject category"
                      />
                      <LinkButton text="Create new subject category" @click="createSubjectCategoryModal = true" />
                    </div>

                    <div class="col-md-12">
                      <div class="editor-wrapper">
                        <span class="stack-label">Content Summary</span>
                        <q-editor
                          id="content_summary"
                          ref="editor"
                          v-model="formData.content_summary"
                          :content-style="{ listStylePosition: 'inside' }"
                          :label="getTranslation('product.fields.content_summary') || 'Content Summary'"
                          :fonts="Q_EDITOR_MIXIN_fonts"
                          :toolbar="Q_EDITOR_MIXIN_toolbarSettings"
                          max-height="100px"
                          name="content_summary"
                          rows="3"
                          toolbar-text-color="grey"
                          toolbar-toggle-color="tertiary"
                          toolbar-flat
                          @paste.native="Q_EDITOR_MIXIN_onPaste"
                        />
                      </div>
                    </div>

                    <div class="col-xs-5">
                      <q-input
                        v-model="$v.formData.online_identifier.$model"
                        :error="$v.formData.online_identifier.$error"
                        :error-message="$v_displayMessage($v.formData.online_identifier)"
                        type="text"
                        label="Online identifier (eISSN/eISBN)"
                        outlined
                      />
                    </div>

                    <div class="col-xs-6">
                      <q-input
                        v-model="$v.formData.print_identifier.$model"
                        :error="$v.formData.print_identifier.$error"
                        :error-message="$v_displayMessage($v.formData.print_identifier)"
                        type="text"
                        label="Print identifier (ISSN/ISBN)"
                        outlined
                      />
                    </div>
                  </div>
                </form>
              </q-card-section>
            </q-tab-panel>

            <q-tab-panel name="metaData">
              <q-card-section :style="{ height: '600px' }">
                <ModalMetaData ref="metaData" :meta-data-fields="metaDataFields" />
              </q-card-section>
            </q-tab-panel>
          </q-tab-panels>
          <q-card-actions align="right">
            <div v-if="newProduct" class="row q-pa-lg q-mr-sm justify-end">
              <q-btn
                :label="returnToSubscription ? 'Return to Subscription Form' : 'Start Acquisition'"
                size="sm"
                color="positive"
                @click="startAcquisition(newProduct.id)"
              />
              <q-btn
                :to="{ name: 'product', params: { id: newProduct.id } }"
                color="tertiary"
                size="sm"
                class="on-right"
                label="Go to product page"
              />
            </div>
          </q-card-actions>
        </template>
      </q-splitter>
    </template>
  </LmDialog>
  <form-create-provider
    v-else-if="showProviderForm"
    :showing.sync="showProviderForm"
    :provider-name="publisherName"
    stay-label="Return to Product Form"
    go-label="Go to Publisher Page"
    namespace="publishers"
    entity-name="publisher"
    @create="setPublisherId"
    @close="showProviderForm = false"
  />
</template>

<script>
import { stripHtmlFromInput } from '@/lib/formatting.utils'
import { LmFormObject } from '@/lib/models/lm-form-object.class'
import { cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'
import Q_EDITOR_MIXIN from '@/mixins/q-editor-mixin'
import { validations } from './validations'
// Components
import EnhancedQSelect from '@components/general/EnhancedQSelect'
import FormCreateProvider from '@components/forms/form-create-provider/FormCreateProvider'
import LinkButton from '@/reusable/base-components/LinkButton'
import PopupField from '@/reusable/PopupField/PopupField'
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'
import ModalMetaData from '@/reusable/ModalMetaData'
import CreateSubjectCategoryModal from '@/reusable/CreateSubjectCategoryModal'

export default {
  name: 'CreateProductForm',
  components: {
    EnhancedQSelect,
    FormCreateProvider,
    LinkButton,
    PopupField,
    LmDialog,
    ModalMetaData,
    CreateSubjectCategoryModal
  },
  mixins: [Q_EDITOR_MIXIN, validations],
  props: {
    showing: Boolean,
    publisher: Object,
    returnToSubscription: Boolean,
    productName: String,
    context: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      namespace: 'products',
      formData: null,
      dataTemplate: {
        name: null,
        platform: null,
        product_type_id: null,
        publisher_id: null,
        owner_id: null,
        main_subject_category_id: null,
        content_summary: '',
        online_identifier: '',
        print_identifier: ''
      },
      newProduct: null,
      showProductTypeForm: false,
      showProviderForm: false,
      productTypeName: null,
      publisherName: null,
      creatingProductType: false,
      openPopupField: false,
      activeTab: 'basic',
      splitterModel: 10,
      createSubjectCategoryModal: false
    }
  },
  computed: {
    ...mapGetters('products', [
      'users',
      'types',
      'publishers',
      'savedNewProduct',
      'publisherId',
      'typeOptions',
      'publisherOptions',
      'userOptions',
      'subjectCategoriesOptions',
      'metaDataFields'
    ]),
    modalShow: {
      get() {
        return this.showing
      },
      set(val) {
        if (!val) {
          this.$emit('close')
          this.activeTab = 'basic'
          this.$v.$reset()
          if (this.$refs.metaData) this.$refs.metaData.resetValidation()
        }
      }
    }
  },
  watch: {
    modalShow() {
      this.initFormData()
    },
    showProviderForm(newVal) {
      if (!newVal) this.formData = cloneDeep(this.savedNewProduct)
      this.formData.publisher_id = this.publisherId
    }
  },
  beforeMount() {
    this.initFormData()
  },
  created() {
    if (!this.types || !this.users || !this.publishers) {
      this.loading = true
      return this.$store.dispatch('products/getCreateFormData').then(() => {
        this.loading = false
      })
    }
  },
  methods: {
    initFormData() {
      this.formData = new LmFormObject(this.dataTemplate)
      this.formData.publisher_id = this.publisher ? this.publisher.id : null
      this.formData.name = this.productName
      if (!this.publishers || !this.owners || !this.types) {
        this.$store.dispatch('products/getCreateFormData')
      }
    },
    prepareFormData() {
      let data = cloneDeep(this.formData)
      delete data.originalData
      delete data.errors
      data.provider = this.entityId
      data.content_summary = stripHtmlFromInput(data.content_summary)

      if (this.$refs.metaData) {
        data.meta_data_fields_by_category = this.$refs.metaData.getUpdatedMetaData()
      } else {
        data.meta_data_fields_by_category = this.metaDataFields
      }

      return data
    },

    async createSubjectCategory(name) {
      const newSubjectCategory = await this.$store.dispatch('products/createSubjectCategory', { name })
      this.formData.main_subject_category_id = newSubjectCategory.id
    },

    submit() {
      if (!this.$v_validationPass()) return
      let formData = this.prepareFormData()
      let params = {
        namespace: this.namespace,
        value: formData,
        context: this.context
      }

      return this.$store.dispatch('products/create', params).then(result => {
        this.newProduct = result
        this.$emit('create', result)
      })
    },
    closeModal() {
      this.modalShow = false
      setTimeout(() => {
        this.initFormData()
        this.$set(this, 'newProduct', null)
      }, 1000)
    },
    async startAcquisition(id) {
      await this.$store.dispatch('subscriptions/subscriptionPeriods/setProductId', id)
      await this.$store.dispatch('subscriptions/subscriptionPeriods/getCreateFormData')
      if (this.returnToSubscription) this.modalShow = false
      else {
        this.$emit('close-with-acquisition', id)
        this.activeTab = 'basic'
        this.$v.$reset()
        if (this.$refs.metaData) this.$refs.metaData.resetValidation()
      }

      setTimeout(() => {
        this.initFormData()
        this.$set(this, 'newProduct', null)
      }, 1000)
    },
    saveCurrentState() {
      this.$store.dispatch('products/setSavedNewProduct', this.formData)
    },
    openPublisherForm() {
      this.saveCurrentState()
      this.showProviderForm = true
    },
    setPublisherId(publisher) {
      this.$store.dispatch('products/setPublisherId', publisher.id)
      this.$store.dispatch('products/getCreateFormData')
    },
    async createProductType(productType) {
      this.creatingProductType = true
      const response = await this.$store.dispatch('productTypes/create', {
        value: { name: productType }
      })
      this.formData.product_type = response.id
      await this.$store.dispatch('products/getCreateFormData')
      this.creatingProductType = false
      this.productTypeName = null
      this.openPopupField = false
    }
  }
}
</script>

<style lang="stylus" scoped>
.q-card {
  width: 1200px;
  max-width: 1200px;
}

.no-vertical-padding{
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
.editor-wrapper {
  display: grid
  grid-gap 15px 0

  .stack-label {
    font-size: 0.75rem;
  }
}
</style>
